// live/preflopRecap.ts (GH8) - surface the auto-played preflop in Observe as a narrated, action-by-action
// RECAP. Preflop ALREADY happens in the engine (generate() auto-plays it via preflopOpenCallAutopilot; the
// full action lives in state.history) - this file adds NO engine logic and makes NO strategy call. It reads
// the recorded preflop history, walks it in action order (which is position order for the opening round -
// UTG..BTN, then the blinds), builds the pot arithmetically as it goes, and pairs each action with the pro's
// live read: what that action means FROM THAT POSITION, and whether it fits or breaks that seat's archetype
// profile (a Rock raising ≠ a Firecracker raising; a Whale limping = wants a cheap flop, on profile). The
// output is a list of <ObserveBeat>s the reused <NarrationBeats> reveals sequentially, plus a board-less
// <FeltView> the reused <OvalFelt> renders - so the recap reuses both components verbatim.
import type { Archetype, ActionKind, Position } from '@postflop/schema';
import type { ObserveBeat } from '../observe/data';
import { type FeltView, POSITION_LABEL, money } from '../engine/bridge';
import { archetypeOf } from '../engine/liveData';
import { displayName, type LiveHandModel } from '../engine/liveRunner';

// ── profile predicates (read straight off the D1 archetype record - the single source of truth) ─────────
const isTight = (a: Archetype): boolean => a.preflop.vpipScale <= 0.85;
const isLoose = (a: Archetype): boolean => a.preflop.vpipScale >= 1.3;
const isLimpy = (a: Archetype): boolean => a.preflop.limpBias >= 0.6;
/** raises/3-bets far more than his share - an open or re-raise from him means less. */
const isAggro = (a: Archetype): boolean =>
  a.imbalances.bettingVolume === 'overBluffs' || a.preflop.threeBetScale >= 1.3;
const overFolds = (a: Archetype): boolean => a.imbalances.bettingVolume === 'overFolds';
const overCalls = (a: Archetype): boolean => a.imbalances.bettingVolume === 'overCalls';
const isBalanced = (a: Archetype): boolean => a.imbalances.bettingVolume === 'balanced';

type PosClass = 'early' | 'middle' | 'late' | 'blind';
const posClassOf = (pos: Position): PosClass => {
  if (pos === 'UTG' || pos === 'UTG1' || pos === 'UTG2' || pos === 'LJ') return 'early';
  if (pos === 'HJ') return 'middle';
  if (pos === 'CO' || pos === 'BTN') return 'late';
  return 'blind';
};
/** the seat's position as it reads in a sentence ("under the gun", "on the button", "in the big blind"). */
const POS_PHRASE: Record<Position, string> = {
  UTG: 'under the gun',
  UTG1: 'under the gun',
  UTG2: 'in early position',
  LJ: 'from the lojack',
  HJ: 'from the hijack',
  CO: 'in the cutoff',
  BTN: 'on the button',
  SB: 'from the small blind',
  BB: 'in the big blind',
};

/** the read context for one villain action - everything preflopReadFor needs beyond the archetype+position. */
export interface ReadContext {
  /** this action is the FIRST voluntary raise of the hand (an open, not a 3-bet+). */
  isOpen: boolean;
  /** this raise re-raises a prior raise (a 3-bet or more). */
  is3betPlus: boolean;
  /** this call completes the big blind with no raise yet (an open-limp). */
  isLimp: boolean;
  /** the big blind checks its option (folded/limped around to it). */
  isBBOption: boolean;
  /** there is already a live raise on the table when this action is taken (a call here is calling a raise). */
  facingRaise: boolean;
  /** PF8: how many TRUE raises preceded this action - a jam counts only above the bb, mirroring engine
   *  `isRaise` (strategy/heroSpot.ts) and `raisesIn` (preflopView.ts). Hero's own re-raise is the
   *  (raiseDepth+1)-bet: 1 ⇒ 3-bet, 2 ⇒ 4-bet, 3 ⇒ 5-bet. Optional - only buildPreflopRecap supplies it;
   *  heroReadFor fixtures that omit it fall back to a plain "re-raise". */
  raiseDepth?: number;
  /** PF8: this raise/jam committed the actor's entire stack, so the beat names the shove ("all in"). The
   *  engine records a max-sized 5-bet as `raise` to the full stack, NOT `jam`, so all-in is detected from
   *  the amount, not the action kind. */
  allIn?: boolean;
}

/**
 * The pro's live read of one VILLAIN preflop action: what it signifies from that position, and how it fits
 * INTO or sits OUTSIDE that player's archetype profile. Pure function of (archetype, position, action, ctx)
 * - the judgment layer GH8 is graded on. Hero's own actions are narrated separately in buildPreflopRecap
 * (the pro reasoning about his own line, not reading himself).
 */
export function preflopReadFor(
  arch: Archetype,
  pos: Position,
  kind: ActionKind,
  ctx: ReadContext,
): string {
  const pc = posClassOf(pos);
  const late = pc === 'late';
  const blind = pc === 'blind';

  if (kind === 'raise' || kind === 'jam' || kind === 'bet') {
    // ── a re-raise (3-bet or more) ────────────────────────────────────────────────────────────────────
    if (ctx.is3betPlus) {
      if (isLimpy(arch)) {
        return `A habitual limper suddenly 3-betting is wildly off profile - this is a player who wants cheap flops, so when he finally re-raises it is a monster he does not want to play soft. Aces or kings, almost never a bluff.`;
      }
      if (arch.preflop.threeBetScale >= 1.3) {
        return `His 3-bet range is huge - this can be aces just as easily as pure air, and on profile it leans bluff-heavy. Do not fold everything to it, and do not pay it off light either.`;
      }
      if (arch.preflop.threeBetScale <= 0.7) {
        return `A re-raise from him is the top of the deck - aces, kings, ace-king, and little else. Dead on profile and about as strong as a preflop signal gets. Get out of the way without a real hand.`;
      }
      return `A balanced 3-bet: mostly genuine hands with a few bluffs mixed in. On profile - respect it and continue only with a strong holding.`;
    }
    // ── an open (the first raise in) ──────────────────────────────────────────────────────────────────
    if (isTight(arch) || overFolds(arch)) {
      const wide = late ? ` He will stretch it a little wider ${POS_PHRASE[pos]}, but not by much.` : '';
      return `A player this tight opening ${POS_PHRASE[pos]} is the tightest range at the table - big pairs and big broadways, almost nothing loose. Exactly on profile: he only raises with a real hand, so the raise itself is the warning.${wide}`;
    }
    if (isAggro(arch)) {
      const wider = late ? ' - and from late position it is wider still' : '';
      return `He raises with a huge range: suited junk, small pairs, plenty of pure air${wider}. Squarely on profile, so this open tells you far less than the same raise from a nit. Do not hand him respect he has not earned.`;
    }
    if (overCalls(arch) && isLimpy(arch)) {
      return `A habitual limper raising instead of limping is off profile, and that is the tell - when a player who likes cheap flops suddenly opens, it is usually a genuinely big hand he does not want to play in a family pot.`;
    }
    if (isBalanced(arch)) {
      return `He opens a solid, position-appropriate range - balanced and hard to attack. On profile: nothing exploitable here, so play it straight and lean on your position.`;
    }
    return `He opens the pot. From ${POS_PHRASE[pos]} that is the range you would expect from his style - no break in the pattern yet.`;
  }

  if (kind === 'call') {
    // ── an open-limp (completing, no raise yet) ───────────────────────────────────────────────────────
    if (ctx.isLimp) {
      if (isLimpy(arch)) {
        return `He limps to see a cheap flop with anything - suited cards, an ace, a baby pair. Dead on profile: he wants in cheap and hopes to get lucky, so his range is wide and weak. No information, and a hand you want to raise and isolate.`;
      }
      return `A limp is off profile for him - he usually raises or folds. When a player like this just completes, it is often a hand he wants to see a flop with cheaply, like a small pair or suited connector. Unusual enough to note.`;
    }
    // ── a call of a raise (a flat / a blind defend) ───────────────────────────────────────────────────
    const defend = blind ? `Defending ${POS_PHRASE[pos]}, ` : '';
    if (isAggro(arch)) {
      return `${defend}he flats instead of his usual 3-bet, which caps him a little - his very best hands would have come in raising. A reasonable call, but his range no longer holds the nuts.`;
    }
    if (overCalls(arch)) {
      const disc = blind ? ' He is getting a price to peel from the blind, so it is wider still.' : '';
      return `${defend}he calls - and he calls too much, so this is just "I want to see a flop." His range stays wide and weak and will pay off value later, so bet for value and do not try to bluff him out.${disc}`;
    }
    if (isTight(arch) || overFolds(arch)) {
      return `${defend}flatting instead of folding, from a player this tight, means a real hand that is not quite a re-raise - middle pairs, suited big cards. On profile: when he puts money in, it is genuine.`;
    }
    return `${defend}he flats the raise, keeping in the hands he would only sometimes re-raise. A sound, position-aware call - nothing to exploit yet.`;
  }

  if (kind === 'check') {
    if (ctx.isBBOption) {
      return `He checks his option in the big blind - in for free, taking a flop with whatever two cards he holds. Zero information, so read him on the flop, not now.`;
    }
    return `He checks it back.`;
  }

  // ── a fold ──────────────────────────────────────────────────────────────────────────────────────────
  if (overFolds(arch)) {
    return `He folds, the way he folds most hands. On profile and no information - he was never really in this pot.`;
  }
  if (isLoose(arch)) {
    return `Even a player this loose lets it go - off profile enough to matter: the hands he does NOT fold from here just tightened up a shade.`;
  }
  return `He folds. One fewer player, and the read narrows to who is left.`;
}

/** PF8: what hero's re-raise is CALLED at raise-depth n (the count of TRUE raises before it): 1 ⇒ 3-bet,
 *  2 ⇒ 4-bet, 3 ⇒ 5-bet, deeper ⇒ a plain "re-raise". n===0 (no prior raise) is an open handled by the
 *  isOpen branch; it lands here only for older fixtures that omit raiseDepth, and "re-raise" is the safe
 *  fallback. Mirrors `raiseFor` in preflopView.ts. */
const reRaiseLabel = (n: number): string =>
  n === 1 ? '3-bet' : n === 2 ? '4-bet' : n === 3 ? '5-bet' : 're-raise';

/** hero's own preflop line (he is the winning reg the pro voice belongs to - he reasons, he does not read
 *  himself). Keyed by what he actually did + whether he was opening or reacting to a raise. */
/**
 * The pro's read of his OWN preflop action.
 *
 * TB2 - this used to open every line with "on the button, last to act every street after this ... position
 * is the edge I press the rest of the way", which was safe only because `baseSpec` pinned hero to the BTN
 * forever (`heroPosition: 'BTN'`). The continuous table rotates the button (TB0 §4.2), so hero now sits in
 * the Cutoff, the blinds, and under the gun - and that copy became a flat lie about the seat he is in and
 * the position he holds. It is graded, teaching prose: it has to describe the actual spot (North Star §4.1
 * - the engine decides what happened, the pro only explains it).
 *
 * So position claims are now earned from `pos`: only the button is last to act, and only the button and the
 * cutoff get to talk about playing the pot in position at all. Out of the blinds the read says the true,
 * opposite thing.
 */
export function heroReadFor(kind: ActionKind, ctx: ReadContext, pos: Position): string {
  const seat = POSITION_LABEL[pos].toLowerCase();
  const isBtn = pos === 'BTN';
  // late position = the only seats that can honestly claim position on the field after the flop.
  const isLate = isBtn || pos === 'CO';
  const oop = pos === 'SB' || pos === 'BB';

  if ((kind === 'raise' || kind === 'jam' || kind === 'bet') && ctx.isOpen) {
    if (isBtn) {
      return `My seat and my hand: on the button, last to act every street after this, I raise to take the betting lead and play the pot in position. Position is the edge I press the rest of the way.`;
    }
    if (isLate) {
      return `My seat and my hand: from the ${seat} I raise to take the betting lead and fold out the blinds. Only the button acts behind me, so I am usually the one with position after the flop.`;
    }
    if (oop) {
      return `My seat and my hand: I raise from the ${seat}. I will be out of position for the rest of the hand, so I want the betting lead and a smaller field to make up for it.`;
    }
    return `My seat and my hand: I raise from the ${seat}. Opening this early means a strong hand and a lot of players still to act, so I take the lead and let the seats behind me tell me where I am.`;
  }
  if (kind === 'raise' || kind === 'jam' || kind === 'bet') {
    // PF8: name the depth (3-bet / 4-bet / 5-bet) and, when it commits the stack, the shove. The action
    // PHRASE carries the exact naming ("I 4-bet to $108"); this read is the reasoning beside it, so it
    // references the move without re-quoting the amount.
    const move = reRaiseLabel(ctx.raiseDepth ?? 0);
    if (ctx.allIn) {
      return isLate
        ? `That ${move} is all in. With the initiative and position, I would rather get the whole stack in now with the best of it than flat and hope to guess right later.`
        : `That ${move} is all in from the ${seat}. Out of position I have nothing to lean on later, so I commit now - get my equity in and deny a cheap, dangerous flop.`;
    }
    return isLate
      ? `My ${move} takes the pot by the throat: with the initiative and position I would rather define it now than flat and guess later.`
      : `I ${move} from the ${seat}. I will not have position, so I would rather define the pot now than flat and play a guessing game out of turn.`;
  }
  if (kind === 'call' && ctx.facingRaise) {
    return isLate
      ? `I call the raise in position rather than start a war out of a marginal open. I keep my equity, keep worse hands in, and let position do the work postflop.`
      : `I call the raise from the ${seat} rather than start a war out of a marginal open. I keep my equity and keep worse hands in - but I am out of position, so the hand has to play itself cheaply.`;
  }
  if (kind === 'call') {
    return isLate
      ? `I just call, keeping the pot small and planning to use position after the flop.`
      : `I just call from the ${seat}, keeping the pot small - without position I want a cheap flop, not a big one.`;
  }
  if (kind === 'check') {
    return `I check and take a free look.`;
  }
  return `I let it go - the wrong spot to get involved.`;
}

// ── the running-pot arithmetic (pure; NOT engine logic - it re-sums recorded commitments) ───────────────
const nWord = (n: number): string =>
  ({ 2: 'Heads-up', 3: 'Three-handed', 4: 'Four-handed', 5: 'Five-handed', 6: 'Six-handed' } as Record<number, string>)[n] ??
  `${n}-handed`;

/** Everything the preflop Observe step renders: a board-less felt for <OvalFelt> and the ordered beats for
 *  <NarrationBeats>. */
export interface PreflopRecap {
  felt: FeltView;
  beats: ObserveBeat[];
  kicker: string;
  h2: string;
  lede: string;
}

/**
 * Build the Observe preflop recap for a live hand. Walks model.terminal.history in action order (= position
 * order for the opening round: first-to-act .. button, then the blinds), tracking each seat's commitment so
 * the pot climbs beat by beat, and pairs every action with the pro's read. Villains get preflopReadFor
 * (profile-fit judgment); hero gets heroReadFor (the pro's own reasoning). NO engine call, NO strategy - it
 * only re-narrates what generate()'s autopilot already produced.
 */
export function buildPreflopRecap(model: LiveHandModel): PreflopRecap {
  const t = model.terminal;
  const players = t.players;
  const { sb, bb } = t.spec.stakes;
  const posOf = new Map<number, Position>(players.map((p) => [p.seat, p.position]));

  // seat -> chips already committed this street. Blinds are posted (init.ts), not in history, so seed them.
  const committed = new Map<number, number>();
  for (const p of players) committed.set(p.seat, p.position === 'SB' ? sb : p.position === 'BB' ? bb : 0);
  let pot = players.reduce((s, p) => s + (committed.get(p.seat) ?? 0), 0);
  let currentBet = bb;
  let raisesSoFar = 0;
  // PF8: each seat's TOTAL starting chips (behind + already posted), from the dealt state. Preflop a
  // raise's "to" amount IS the seat's whole-hand commitment, so a raise-to that equals this is all in -
  // which is how a max-sized 5-bet (recorded as `raise`, not `jam`) is caught. `trueRaisesSoFar` counts
  // raises the way the engine does (a jam counts only above the bb), so hero's re-raise depth is named
  // right: it is the (trueRaisesSoFar+1)-bet at the moment he acts.
  const startingChips = new Map<number, number>(model.initial.players.map((p) => [p.seat, p.stack + p.committedTotal]));
  let trueRaisesSoFar = 0;

  const beats: ObserveBeat[] = [];
  // framing beat: the stakes, the posted blinds, the pot before a card is looked at.
  beats.push({
    tokens: [
      `${nWord(players.length)}. Blinds post $${sb} and $${bb} - `,
      { num: money(pot) },
      ` in the middle before a card is read. Now the read, seat by seat, as the betting comes around.`,
    ],
  });

  const preflop = t.history.filter((h) => h.street === 'preflop');
  for (const h of preflop) {
    const a = h.action;
    const seat = a.seat;
    const pos = posOf.get(seat)!;
    const prior = committed.get(seat) ?? 0;
    const isHero = seat === model.heroSeat;

    // running pot: a raise/bet tops the bet up to its "to" amount; a call matches currentBet; else nothing.
    let toAmt = currentBet;
    if ((a.kind === 'raise' || a.kind === 'bet' || a.kind === 'jam') && a.amount !== undefined) {
      pot += a.amount - prior;
      committed.set(seat, a.amount);
      toAmt = a.amount;
      currentBet = Math.max(currentBet, a.amount);
    } else if (a.kind === 'call') {
      pot += currentBet - prior;
      committed.set(seat, currentBet);
      toAmt = currentBet - prior; // the amount he put in to call
    }

    const isRaise = a.kind === 'raise' || a.kind === 'bet' || a.kind === 'jam';
    // PF8: mirror engine `isRaise`/preflopView `raisesIn` - a jam is a raise only when it exceeds the bb.
    const isTrueRaise = a.kind === 'raise' || a.kind === 'bet' || (a.kind === 'jam' && (a.amount ?? 0) > bb);
    const ctx: ReadContext = {
      isOpen: isRaise && raisesSoFar === 0,
      is3betPlus: isRaise && raisesSoFar >= 1,
      isLimp: a.kind === 'call' && raisesSoFar === 0 && currentBet === bb,
      isBBOption: a.kind === 'check' && pos === 'BB',
      facingRaise: raisesSoFar >= 1,
      // depth = true raises BEFORE this action; all in = the raise-to commits the seat's whole stack.
      raiseDepth: trueRaisesSoFar,
      allIn: isRaise && a.amount !== undefined && a.amount === startingChips.get(seat),
    };
    if (isRaise) raisesSoFar += 1;
    if (isTrueRaise) trueRaisesSoFar += 1;

    // the action phrase (villain: third person; hero: first person).
    const phrase = actionPhrase(a.kind, ctx, toAmt, a.amount, isHero);
    const archId = players.find((p) => p.seat === seat)?.archetypeId;
    const read =
      isHero || !archId ? heroReadFor(a.kind, ctx, pos) : preflopReadFor(archetypeOf(archId), pos, a.kind, ctx);
    const name = isHero || !archId ? 'You' : displayName(archId);

    if (isHero) {
      beats.push({
        decision: true,
        tag: ctx.isOpen ? 'My open' : 'My move',
        // TB2: hero's seat is NAMED, not assumed. This literally read "On the button, ..." - true only
        // while baseSpec pinned hero to the BTN. The continuous table rotates him, so the beat said "On
        // the button, I open to $12" under a felt labelled "You, Cutoff". Villain beats already used the
        // real POSITION_LABEL; hero's now does the same.
        tokens: [`${POSITION_LABEL[pos]} - ${phrase}. `, read, ` Pot `, { num: money(pot) }, `.`],
      });
    } else {
      beats.push({
        tokens: [`${POSITION_LABEL[pos]} - ${name} ${phrase}. `, read, ` Pot `, { num: money(pot) }, `.`],
      });
    }
  }

  return {
    felt: preflopFelt(model, pot),
    beats,
    kicker: 'Preflop',
    h2: 'Read the table before the flop',
    lede: 'Every seat acts. What each one does - and whether it fits his profile - is the read you carry to the flop.',
  };
}

/** FULL-B: how a preflop-only hand actually ENDED - the three outcomes the copy has to tell apart. */
export type PreflopOnlyOutcome = 'folded' | 'all_in' | 'uncontested';

/**
 * FULL-B: classify a preflop-only hand (`model.snapshots.length === 0` - hero has no postflop DECISION).
 *
 * Before FULL, hero's line was check/call-never-raise, so the ONLY way a hand ended preflop was hero
 * folding, and "the pro is out" was always true. Hero now plays the pro's line and can jam preflop, so
 * "no postflop decision" no longer implies "not in the hand". Three real outcomes, in this ORDER:
 *
 *  1. `folded`      - hero folded preflop. Checked FIRST and it must stay first: TB4's F4 call has the
 *                     table play ON after hero folds, so a folded hand still runs a board out (measured:
 *                     board 0/3/4/5 all occur on folded hands). Any board-length test ahead of this one
 *                     would call a fold an all-in.
 *  2. `all_in`      - hero never folded and a board exists: the money went in preflop and the hand ran
 *                     out with nothing left to decide. NOT keyed on `hero.allIn` - when hero COVERS the
 *                     villain, the villain is all-in and hero is not, yet neither has a decision left.
 *  3. `uncontested` - hero never folded and there is no board: everyone folded to him.
 *
 * Total by construction - a hand hero is still live in with no postflop decision has either run out or
 * ended right there, so the `all_in` fallback cannot mislabel a fourth case into existence.
 */
export function preflopOnlyOutcome(model: LiveHandModel): PreflopOnlyOutcome {
  const hero = model.terminal.players.find((p) => p.seat === model.heroSeat);
  if (hero?.folded) return 'folded';
  return model.terminal.board.length === 0 ? 'uncontested' : 'all_in';
}

/**
 * FULL-B: the Observe lede for a preflop-only hand. This slot used to be one hardcoded string that assumed
 * every preflop-only hand was a fold - it read "The pro is out before the flop" over a hand the pro won
 * $310 in (`?seed=full-demo&mode=observe`, hand 1: he 3-bets, 5-bet jams, gets called, the board runs out).
 * The pot was real and honestly won; only the sentence was wrong.
 *
 * Copy only - it reads what the hand DID (`preflopOnlyOutcome`) and makes no engine or strategy call.
 */
export function preflopOnlyLede(model: LiveHandModel): string {
  const net = model.heroNetChips;
  switch (preflopOnlyOutcome(model)) {
    case 'folded':
      return 'The pro is out before the flop. The read still counts - what every seat did here is what you carry to the next hand.';
    case 'all_in': {
      // a lost all-in is not a bad read, so the result is reported and the read is what carries either way.
      const result = net > 0 ? `he won ${money(net)}` : net < 0 ? `he lost ${money(-net)}` : 'the pot chopped';
      return `The pro got it in before the flop and the board ran out behind it - ${result}. There was no decision left after the flop: the read that got the money in is the whole hand, and it is what you carry to the next one.`;
    }
    case 'uncontested':
      return `Nobody came along. The pro takes it before the flop, up ${money(net)} - who folded, and how fast, is the read you carry to the next hand.`;
  }
}

/**
 * A single compact "how the pot got to the flop" line for the Diagnose/Solo entry (GH9). Diagnose and Solo
 * are postflop-first DECISION surfaces - they open on the flop by design - but with no orientation the flop
 * reads as being dumped mid-hand. This grounds it: one terse sentence of the preflop line (hero + villains,
 * folds elided) and the pot carried to the flop. It reuses the SAME recorded preflop history + running-pot
 * arithmetic as buildPreflopRecap and makes NO engine/strategy call. Returns null if there is no recorded
 * preflop action (a synthesized hand), so the caller can omit the strip cleanly.
 */
export function preflopContextLine(model: LiveHandModel): string | null {
  const t = model.terminal;
  const preflop = t.history.filter((h) => h.street === 'preflop');
  if (preflop.length === 0) return null;
  const players = t.players;
  const { sb, bb } = t.spec.stakes;

  // seed blinds (posted, not in history), then re-walk the preflop commitments so the pot climbs the same
  // way the recap builds it. Each voluntary action becomes a terse clause; folds are elided to keep it one line.
  const committed = new Map<number, number>();
  for (const p of players) committed.set(p.seat, p.position === 'SB' ? sb : p.position === 'BB' ? bb : 0);
  let currentBet = bb;
  let raises = 0;
  const clauses: string[] = [];

  for (const h of preflop) {
    const a = h.action;
    const prior = committed.get(a.seat) ?? 0;
    const isHero = a.seat === model.heroSeat;
    const isRaise = a.kind === 'raise' || a.kind === 'bet' || a.kind === 'jam';
    const callAmt = currentBet - prior; // what a call costs him here (before we advance the pot)

    if (isRaise && a.amount !== undefined) {
      committed.set(a.seat, a.amount);
      currentBet = Math.max(currentBet, a.amount);
    } else if (a.kind === 'call') {
      committed.set(a.seat, currentBet);
    }

    const isOpen = isRaise && raises === 0;
    const is3betPlus = isRaise && raises >= 1;
    const isLimp = a.kind === 'call' && raises === 0 && currentBet === bb;
    if (isRaise) raises += 1;
    if (a.kind === 'fold') continue; // folds are implied by who is left; keep the line tight

    const archId = players.find((p) => p.seat === a.seat)?.archetypeId;
    const who = isHero || !archId ? 'You' : displayName(archId);
    clauses.push(`${who} ${contextVerb(a.kind, { isOpen, is3betPlus, isLimp }, a.amount, callAmt, isHero)}`);
  }

  if (clauses.length === 0) return null;
  const pot = [...committed.values()].reduce((s, v) => s + v, 0);
  return `${clauses.join(', ')}. ${money(pot)} to the flop.`;
}

/** the terse verb phrase for the preflop-context line (no reads, no first-person "I" - every seat including
 *  hero reads in the same "<who> opens/3-bets/calls" shape so the one-liner scans cleanly). */
function contextVerb(
  kind: ActionKind,
  ctx: { isOpen: boolean; is3betPlus: boolean; isLimp: boolean },
  raiseTo: number | undefined,
  callAmt: number,
  isHero: boolean,
): string {
  const s = isHero ? '' : 's'; // third-person 's' for villains ("opens"), none for hero ("You open")
  const to = money(raiseTo ?? 0);
  if (kind === 'raise' || kind === 'bet') {
    if (ctx.isOpen) return `open${s} to ${to}`;
    if (ctx.is3betPlus) return `3-bet${s} to ${to}`;
    return `raise${s} to ${to}`;
  }
  if (kind === 'jam') return `jam${s} all in`;
  if (kind === 'call') return ctx.isLimp ? `limp${s} in` : `call${s} ${money(callAmt)}`;
  if (kind === 'check') return `check${s}`;
  return `fold${s}`;
}

/** the plain-English action phrase. Hero uses the first person ("I open to $8"); villains the third
 *  ("opens to $8", "limps in", "calls $8", "3-bets to $26", "folds"). */
function actionPhrase(
  kind: ActionKind,
  ctx: ReadContext,
  callAmt: number,
  raiseTo: number | undefined,
  isHero: boolean,
): string {
  const to = money(raiseTo ?? 0);
  if (kind === 'raise' || kind === 'bet') {
    if (ctx.isOpen) return isHero ? `I open to ${to}` : `opens to ${to}`;
    if (ctx.is3betPlus) {
      // PF8: was hardcoded "3-bet" for EVERY re-raise, so a 4-bet and an all-in 5-bet both read "3-bet".
      // Depth-aware now (3-bet / 4-bet / 5-bet), and a raise that commits the whole stack is named a shove
      // - the engine records a max 5-bet as `raise` to the full stack, not `jam`, so this catches it too.
      const move = reRaiseLabel(ctx.raiseDepth ?? 0);
      if (ctx.allIn) return isHero ? `I ${move} all in to ${to}` : `${move}s all in to ${to}`;
      return isHero ? `I ${move} to ${to}` : `${move}s to ${to}`;
    }
    return isHero ? `I raise to ${to}` : `raises to ${to}`;
  }
  if (kind === 'jam') return isHero ? `I jam all in` : `jams all in`;
  if (kind === 'call') {
    if (ctx.isLimp) return isHero ? `I limp along` : `limps in`;
    return isHero ? `I call ${money(callAmt)}` : `calls ${money(callAmt)}`;
  }
  if (kind === 'check') {
    if (ctx.isBBOption) return isHero ? `I check my option` : `checks the option`;
    return isHero ? `I check` : `checks`;
  }
  return isHero ? `I fold` : `folds`;
}

/** A board-less felt for the preflop step: the same live ring/geometry (reused verbatim) with the community
 *  cards cleared and the pot set to what the preflop built. Derived from the first snapshot's felt (which
 *  already carries the display names, hero cards, and button) so nothing about the ring is rewritten. */
function preflopFelt(model: LiveHandModel, potEnd: number): FeltView {
  // TB2: prefer the flop felt, but fall back to hero's PREFLOP decision felt before giving up on seats.
  // This used to read snapshots[0] only, and its no-snapshot fallback rendered `seats: []` - an empty
  // table with nobody sitting at it. That was survivable while a preflop-only hand was unreachable (hero
  // pinned to the button, the autopilot never folding); the continuous table makes hero fold preflop
  // routinely, and six opponents blinking out of existence is the opposite of what this screen is for.
  // `model.preflop.felt` is the same engine-built felt, one street earlier, with every seat real.
  const base = model.snapshots[0]?.felt ?? model.preflop?.felt;
  if (!base) {
    // genuinely nothing to draw from (no postflop snapshot AND no played preflop step) - hero's own row
    // is still honest, so show that rather than nothing.
    const hero = model.terminal.players.find((p) => p.seat === model.heroSeat)!;
    return {
      seats: [],
      board: [],
      pot: money(potEnd),
      heroCards: model.heroCards,
      heroLabel: `You, ${POSITION_LABEL[hero.position]}`,
      heroHasButton: model.terminal.buttonSeat === model.heroSeat,
    };
  }
  return {
    ...base,
    board: [],
    pot: money(potEnd),
    // clear the flop aggressor highlight/chip - preflop has its own rhythm in the beats, not on the felt.
    seats: base.seats.map((s) => ({ ...s, active: false, bet: '' })),
  };
}
